import React from "react";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import { Typography, CircularProgress, IconButton, Chip } from "@mui/material";
import makeStyles from "utils/makeStyles";
import CloseIcon from "@mui/icons-material/Close";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: "#fff",
    boxShadow: theme.shadows[12],
    borderRadius: 16,
    width: 560,
    maxWidth: "92vw",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px",
    borderBottom: "1px solid #EFEFF2",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 700,
    fontSize: 18,
    color: "#1A1A1A",
  },
  alertBanner: {
    margin: "16px 20px 0",
    padding: "12px 14px",
    borderRadius: 10,
    backgroundColor: "#FFF4F2",
    borderLeft: "4px solid #E3492B",
  },
  body: {
    padding: "16px 20px 20px",
    overflowY: "auto",
    overflowX: "hidden",
    // Without this a long child can force the flex item wider than the modal
    // instead of wrapping, pushing text outside the box.
    minWidth: 0,
  },
  section: {
    marginBottom: 18,
    minWidth: 0,
  },
  sectionHeading: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: "#6B6B6F",
    marginBottom: 6,
  },
  sectionBody: {
    fontSize: 14.5,
    color: "#26262B",
    lineHeight: 1.6,
    // break-word (not "anywhere"): wrap at word boundaries normally and
    // only split a word when it genuinely cannot fit, e.g. a long URL.
    overflowWrap: "break-word",
  },
  richText: {
    fontSize: 14.5,
    color: "#26262B",
    lineHeight: 1.6,
    overflowWrap: "break-word",
    counterReset: "list-0 list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9",
    "& p": { margin: "0 0 6px" },

    // Quill 2 stores BOTH bullet and numbered lists as <ol><li data-list="...">
    // and draws the marker from CSS scoped to .ql-editor. Outside the editor
    // that CSS does not apply, so bullets lost their marker and the empty
    // .ql-ui span pushed text onto its own line. Mirror Quill's own rules so
    // stored content renders here exactly as it does in the editor.
    "& ol, & ul": { margin: "4px 0", paddingLeft: "1.5em" },
    "& li": {
      listStyleType: "none",
      paddingLeft: "1.5em",
      position: "relative",
      marginBottom: 4,
    },
    "& li > .ql-ui": { position: "absolute" },
    "& li > .ql-ui:before": {
      display: "inline-block",
      marginLeft: "-1.5em",
      marginRight: ".3em",
      textAlign: "right",
      whiteSpace: "nowrap",
      width: "1.2em",
    },
    '& li[data-list="bullet"] > .ql-ui:before': { content: '"\\2022"' },
    '& li[data-list="checked"] > .ql-ui:before': { content: '"\\2611"' },
    '& li[data-list="unchecked"] > .ql-ui:before': { content: '"\\2610"' },
    '& li[data-list="ordered"]': { counterIncrement: "list-0" },
    '& li[data-list="ordered"] > .ql-ui:before': { content: 'counter(list-0, decimal) ". "' },
    // Lists authored outside Quill (plain <ul>/<ol> without data-list) still
    // need their native markers back, since the rule above removes them.
    "& ul > li:not([data-list])": { listStyleType: "disc", paddingLeft: 0, marginLeft: 0 },
    "& ol > li:not([data-list])": { listStyleType: "decimal", paddingLeft: 0, marginLeft: 0 },

    // Rich text can carry pasted images, wide tables or <pre> blocks; keep
    // them inside the modal instead of widening it.
    "& img": { maxWidth: "100%", height: "auto" },
    "& pre": { whiteSpace: "pre-wrap", overflowWrap: "break-word", margin: "0 0 6px" },
    "& table": { maxWidth: "100%", display: "block", overflowX: "auto" },
    "& a": { overflowWrap: "break-word" },
  },
  supportLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "#1a73e8",
    fontWeight: 500,
    textDecoration: "none",
    wordBreak: "break-all",
  },
  emptyState: {
    padding: "24px 4px",
    textAlign: "center",
    color: "#6B6B6F",
  },
}));

const SECTIONS = [
  { key: "description", label: "Description", Icon: InfoOutlinedIcon, richText: false },
  { key: "troubleshooting_steps", label: "Troubleshooting Steps", Icon: ChecklistOutlinedIcon, richText: true },
  { key: "additional_notes", label: "Additional Notes", Icon: StickyNote2OutlinedIcon, richText: false },
];

// Quill saves "empty" content as e.g. "<p><br></p>", which is truthy but
// has no visible text - strip tags before deciding whether to show a section.
const isBlank = (value) => !value || !String(value).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

// A block whose entire text is just a bullet character - never meaningful on
// its own, always a marker for the block that follows.
const BULLET_ONLY = /^[-–—*•·]+$/;

/**
 * Steps pasted from a manual often arrive with the bullet on its own line, so
 * they store as <p>-</p><p>text</p> and render as a stray dash above the step.
 * Merge each such pair into a real list item. Content that already uses proper
 * lists is returned untouched.
 */
const normaliseBulletParagraphs = (html) => {
  if (!html || typeof window === "undefined" || !window.DOMParser) return html;

  let blocks;
  try {
    blocks = Array.from(
      new DOMParser().parseFromString(`<body>${html}</body>`, "text/html").body.children
    );
  } catch (e) {
    return html;
  }
  if (!blocks.length) return html;

  const parts = [];
  let merged = false;
  for (let i = 0; i < blocks.length; i += 1) {
    const text = (blocks[i].textContent || "").replace(/ /g, " ").trim();
    const next = blocks[i + 1];
    if (BULLET_ONLY.test(text) && next) {
      parts.push({ li: next.innerHTML });
      i += 1;
      merged = true;
    } else {
      parts.push({ raw: blocks[i].outerHTML });
    }
  }
  if (!merged) return html;

  let out = "";
  let listOpen = false;
  parts.forEach((part) => {
    if (part.li !== undefined) {
      if (!listOpen) { out += "<ul>"; listOpen = true; }
      out += `<li>${part.li}</li>`;
    } else {
      if (listOpen) { out += "</ul>"; listOpen = false; }
      out += part.raw;
    }
  });
  return listOpen ? `${out}</ul>` : out;
};

const SolutionGuideModal = ({ open, close, loading, guide, alertType, errorMessage }) => {
  const classes = useStyles();

  return (
    <Modal
      aria-labelledby="solution-guide-modal-title"
      className={classes.modal}
      open={!!open}
      onClose={close}
      closeAfterTransition
    >
      <Fade in={!!open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <span className={classes.headerTitle} id="solution-guide-modal-title">
              <ReportProblemOutlinedIcon style={{ color: "#E3492B" }} />
              Solution Guide
            </span>
            <IconButton size="small" onClick={close} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {(alertType || errorMessage) && (
            <div className={classes.alertBanner}>
              {alertType && (
                <Chip
                  size="small"
                  label={alertType}
                  style={{ backgroundColor: "#E3492B", color: "#fff", fontWeight: 600, marginBottom: errorMessage ? 8 : 0 }}
                />
              )}
              {errorMessage && (
                <Typography style={{ fontSize: 14, fontWeight: 500, color: "#3A1A12", marginTop: alertType ? 4 : 0, overflowWrap: "break-word" }}>
                  {errorMessage}
                </Typography>
              )}
            </div>
          )}

          <div className={classes.body}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <CircularProgress size={28} />
              </div>
            ) : !guide ? (
              <div className={classes.emptyState}>
                <Typography>No solution guide is available for this error yet.</Typography>
              </div>
            ) : (() => {
              const visibleSections = SECTIONS.filter((s) => !isBlank(guide[s.key]));
              const hasSupportUrl = !isBlank(guide.support_url);
              if (!visibleSections.length && !hasSupportUrl) {
                return (
                  <div className={classes.emptyState}>
                    <Typography>No additional details have been added for this guide yet.</Typography>
                  </div>
                );
              }
              return (
                <>
                  {visibleSections.map(({ key, label, Icon, richText }) => (
                    <div className={classes.section} key={key}>
                      <div className={classes.sectionHeading}>
                        <Icon fontSize="small" />
                        {label}
                      </div>
                      {richText ? (
                        <div
                          className={classes.richText}
                          dangerouslySetInnerHTML={{ __html: normaliseBulletParagraphs(guide[key]) }}
                        />
                      ) : (
                        <Typography className={classes.sectionBody} style={{ whiteSpace: "pre-wrap" }}>
                          {guide[key]}
                        </Typography>
                      )}
                    </div>
                  ))}
                  {hasSupportUrl && (
                    <div className={classes.section}>
                      <div className={classes.sectionHeading}>
                        <LinkOutlinedIcon fontSize="small" />
                        Support
                      </div>
                      <a
                        href={guide.support_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.supportLink}
                      >
                        {guide.support_url}
                      </a>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default SolutionGuideModal;
